import { apiRequest } from "./apiClient";

const MAPKIT_SCRIPT_ID = "fp-mapkit-js-core";
const MAPKIT_SCRIPT_URL = "https://cdn.apple-mapkit.com/mk/6/mapkit.core.js";
// Mobil ağda token ve Apple CDN yüklemesi aynı anda 5 saniyeyi aşabiliyor.
// Bu süre gerçek bir servis arızasını gizlemeden geçici ağ gecikmesine tolerans verir.
const MAPKIT_LOAD_TIMEOUT_MS = 12000;

let loadPromise = null;

const requestMapKitToken = async () => {
  const response = await apiRequest("/mapkit/token", {
    headers: { "X-MapKit-Origin": window.location.origin },
  });
  const token = String(response?.token || "").trim();
  if (!token) throw new Error("MAPKIT_TOKEN_MISSING");
  return token;
};

const withTimeout = (promise, timeoutMs) => new Promise((resolve, reject) => {
  const timer = window.setTimeout(() => reject(new Error("MAPKIT_LOAD_TIMEOUT")), timeoutMs);
  promise.then(
    (value) => { window.clearTimeout(timer); resolve(value); },
    (error) => { window.clearTimeout(timer); reject(error); }
  );
});

export async function loadMapKitJs(language = "tr") {
  if (window.mapkit?.Map && window.mapkit?.MarkerAnnotation) return window.mapkit;
  if (loadPromise) return loadPromise;

  loadPromise = withTimeout((async () => {
    const tokenPromise = requestMapKitToken();
    let script = document.getElementById(MAPKIT_SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = MAPKIT_SCRIPT_ID;
      script.src = MAPKIT_SCRIPT_URL;
      script.crossOrigin = "anonymous";
      script.async = true;
    }

    const scriptPromise = !window.mapkit
      ? new Promise((resolve, reject) => {
        const loaded = () => resolve();
        const failed = () => reject(new Error("MAPKIT_SCRIPT_FAILED"));
        script.addEventListener("load", loaded, { once: true });
        script.addEventListener("error", failed, { once: true });
        if (!script.isConnected) document.head.appendChild(script);
      })
      : Promise.resolve();

    const [initialToken] = await Promise.all([tokenPromise, scriptPromise]);
    let nextToken = initialToken;

    if (!window.mapkit) throw new Error("MAPKIT_GLOBAL_MISSING");
    window.mapkit.init({
      language: language === "en" ? "en" : "tr",
      authorizationCallback(done) {
        if (nextToken) {
          const token = nextToken;
          nextToken = "";
          done(token);
          return;
        }
        requestMapKitToken().then(done).catch(() => done(""));
      },
    });
    if (typeof window.mapkit.load === "function") await window.mapkit.load(["full-map"]);
    if (!window.mapkit.Map || !window.mapkit.MarkerAnnotation) throw new Error("MAPKIT_LIBRARY_MISSING");
    return window.mapkit;
  })(), MAPKIT_LOAD_TIMEOUT_MS).catch((error) => {
    const failedScript = document.getElementById(MAPKIT_SCRIPT_ID);
    if (!window.mapkit && failedScript) failedScript.remove();
    loadPromise = null;
    throw error;
  });

  return loadPromise;
}

export function preloadMapKitJs(language = "tr") {
  return loadMapKitJs(language);
}

const mapKitCoordinate = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : undefined;
};

const mapKitLanguage = (lang) => lang === "en" ? "en-US" : "tr-TR";

const mapKitRegion = (region) => {
  const latitude = Number(region?.center?.latitude);
  const longitude = Number(region?.center?.longitude);
  const latitudeDelta = Number(region?.span?.latitudeDelta);
  const longitudeDelta = Number(region?.span?.longitudeDelta);
  if (![latitude, longitude, latitudeDelta, longitudeDelta].every(Number.isFinite)) return undefined;
  return {
    center: { latitude, longitude },
    span: { latitudeDelta, longitudeDelta },
  };
};

const normalizeMapKitPlace = (place, source = "apple") => {
  const lines = Array.isArray(place?.displayLines) ? place.displayLines.filter(Boolean) : [];
  const coordinate = place?.coordinate;
  const name = place?.name || lines[0] || place?.formattedAddress || "";
  const subtitle = place?.formattedAddress || lines.slice(1).join(", ") || "";
  return {
    name,
    subtitle,
    lat: Number.isFinite(Number(coordinate?.latitude)) ? Number(coordinate.latitude) : null,
    lng: Number.isFinite(Number(coordinate?.longitude)) ? Number(coordinate.longitude) : null,
    source,
    mapKitResult: place,
  };
};

const loadMapKitSearch = async (lang) => {
  const mk = await loadMapKitJs(lang);
  if (!mk.Search && typeof mk.load === "function") await mk.load(["services"]);
  if (!mk.Search) throw new Error("MAPKIT_SEARCH_LIBRARY_MISSING");
  return mk;
};

export async function autocompleteMapKitPlaces(query, { lang = "tr", lat, lng, region, limit = 8, signal } = {}) {
  const mk = await loadMapKitSearch(lang);
  const coordinate = mapKitCoordinate(lat, lng);
  const searchRegion = mapKitRegion(region);
  const language = mapKitLanguage(lang);
  const search = new mk.Search({ coordinate, region: searchRegion, language });
  const response = await search.autocomplete(String(query || "").trim(), {
    coordinate,
    region: searchRegion,
    regionPriority: mk.RegionPriority?.Default || "default",
    language,
    includeAddresses: true,
    includePhysicalFeatures: true,
    includePointsOfInterest: true,
    includeQueries: false,
    signal,
  });
  return (response?.results || []).slice(0, limit).map((item) => normalizeMapKitPlace(item, "apple-autocomplete"));
}

export async function searchMapKitPlaces(queryOrSuggestion, { lang = "tr", lat, lng, region, limit = 8, signal } = {}) {
  const mk = await loadMapKitSearch(lang);
  const coordinate = mapKitCoordinate(lat, lng);
  const searchRegion = mapKitRegion(region);
  const language = mapKitLanguage(lang);
  const search = new mk.Search({ coordinate, region: searchRegion, language });
  const rawQuery = queryOrSuggestion?.mapKitResult || queryOrSuggestion;
  const response = await search.search(rawQuery, {
    coordinate,
    region: searchRegion,
    regionPriority: mk.RegionPriority?.Default || "default",
    language,
    includeAddresses: true,
    includePhysicalFeatures: true,
    includePointsOfInterest: true,
    signal,
  });
  return (response?.places || []).slice(0, limit).map((item) => normalizeMapKitPlace(item));
}

export function isMapKitQuotaOrAuthError(event) {
  const status = String(event?.status || event?.message || event?.error?.message || "").toLowerCase();
  return status.includes("too many requests") || status.includes("429") ||
    status.includes("unauthorized") || status.includes("token") || status.includes("configuration");
}
